import React from 'react';
import {Button, Card, Checkbox, Divider, Form, Input, Space, Typography} from 'antd';
import {FacebookOutlined, GoogleOutlined, LockOutlined, MailOutlined, UserOutlined} from '@ant-design/icons';
import '../../styles/RegisterForm.css';
import {Link, useNavigate} from 'react-router-dom';
import {useAuth} from "../../contexts/AuthContext";
import { useError } from '../../contexts/ErrorContext';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import register from "../../service/registerService";

const {Title, Text} = Typography;

const RegisterForm: React.FC = () => {
    const [form] = Form.useForm();
    const [googleLoading, setGoogleLoading] = React.useState(false);
    const navigate = useNavigate();
    const { initiateGoogleLogin } = useAuth();
    const { showError } = useError();
    const { loading, handleAsync } = useErrorHandler();

    const onFinish = async (values: any) => {
        const result = await handleAsync(
            async () => {
                await register(values.email, values.password, values.name);
                return true;
            }
        );

        if (result) {
            navigate('/login');
        }
    };

    const handleGoogleLogin = async () => {
        setGoogleLoading(true);
        try {
            await initiateGoogleLogin();
        } catch (error) {
            showError('Failed to initiate Google login');
            setGoogleLoading(false);
        }
    };

    return (
        <Card
            className="register-form-card"
            variant="borderless"
            style={{
                width: '100%',
                maxWidth: '450px',
                borderRadius: '8px'
            }}
            styles={{ body: { padding: '32px' } }}
        >
            <div style={{textAlign: 'center', marginBottom: '24px'}}>
                <Title level={3}>Create Your Account</Title>
                <Text type="secondary">Join our community today</Text>
            </div>

            <Form
                form={form}
                onFinish={onFinish}
                layout="vertical"
                size="large"
            >
                <Form.Item
                    name="name"
                    rules={[{required: true, message: 'Please input your name!'}]}
                >
                    <Input prefix={<UserOutlined/>} placeholder="Full Name"/>
                </Form.Item>

                <Form.Item
                    name="email"
                    rules={[
                        {required: true, message: 'Please input your email!'},
                        {type: 'email', message: 'Please enter a valid email!'},
                    ]}
                >
                    <Input prefix={<MailOutlined/>} placeholder="Email Address"/>
                </Form.Item>

                <Form.Item
                    name="password"
                    rules={[
                        {required: true, message: 'Please input your password!'},
                        {min: 8, message: 'Password must be at least 8 characters!'}
                    ]}
                >
                    <Input.Password prefix={<LockOutlined/>} placeholder="Password"/>
                </Form.Item>

                <Form.Item
                    name="confirm"
                    dependencies={['password']}
                    rules={[
                        {required: true, message: 'Please confirm your password!'},
                        ({getFieldValue}) => ({
                            validator(_, value) {
                                if (!value || getFieldValue('password') === value) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error('The two passwords do not match!'));
                            },
                        }),
                    ]}
                >
                    <Input.Password prefix={<LockOutlined/>} placeholder="Confirm Password"/>
                </Form.Item>

                <Form.Item name="agreement" valuePropName="checked"
                           rules={[{required: true, message: 'You must accept the terms'}]}>
                    <Checkbox>
                        I agree to the <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy
                        Policy</Link>
                    </Checkbox>
                </Form.Item>

                <Form.Item style={{marginTop: '24px'}}>
                    <Button
                        type="primary"
                        htmlType="submit"
                        block
                        loading={loading}
                        size="large"
                    >
                        Create Account
                    </Button>
                </Form.Item>
            </Form>

            <Divider plain>or sign up with</Divider>

            <Space size="large" style={{width: '100%', justifyContent: 'center'}}>
                <Button 
                    shape="circle" 
                    icon={<GoogleOutlined/>}
                    onClick={handleGoogleLogin}
                    loading={googleLoading}
                    style={{
                        backgroundColor: '#db4437',
                        borderColor: '#db4437',
                        color: '#fff'
                    }}
                />
                <Button 
                    shape="circle" 
                    icon={<FacebookOutlined/>}
                    style={{
                        backgroundColor: '#1877f2',
                        borderColor: '#1877f2',
                        color: '#fff'
                    }}
                />
            </Space>

            <div style={{textAlign: 'center', marginTop: '24px'}}>
                <Text>Already have an account? <Link to="/login">Sign in</Link></Text>
            </div>
        </Card>
    );
};

export default RegisterForm;